// Drizzle schema definitions for the primary domain tables.
// Authoritative data model (ADR-007). Zod schemas are derived
// via drizzle-zod (see `./zod`); never hand-maintain parallel ones.

import { sql } from 'drizzle-orm'
import {
  boolean, check, foreignKey, index, integer, numeric, pgEnum,
  pgTable, timestamp, uniqueIndex, uuid, varchar,
} from 'drizzle-orm/pg-core'

import { createdAtColumn, updatedAtColumn } from './schema-shared'
// `buildingTypes` lives in the sibling template-tables file; the
// reference is wrapped in a lazy callback so the cross-file edge
// is resolved after both modules finish loading.
import { buildingTypes } from './schema-templates'

export const groupingConventionEnum = pgEnum('grouping_convention', [
  '1 asset / space',
  '1 asset / building',
  '1 asset / site',
])

export const unitOfMeasureEnum = pgEnum('unit_of_measure', [
  'Count',
  'm',
  'm2',
  'm3',
])

export const buildingStatusEnum = pgEnum('building_status', [
  'not_started',
  'in_progress',
  'complete',
  // Admin-only statuses (see constants/building-status.ts): automatic
  // session-driven status recomputation must never move a building into
  // or out of these — only the admin building update endpoint sets/clears
  // them, and field capture lists hide buildings carrying them.
  'to_be_demolished',
  'out_of_scope',
])

export const userRoleEnum = pgEnum('user_role', ['field_staff', 'admin'])

// users — local cache of authenticated Entra identities.
export const users = pgTable(
  'users',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    entraOid: varchar('entra_oid', { length: 100 }).notNull().unique(),
    displayName: varchar('display_name', { length: 255 }).notNull(),
    role: userRoleEnum('role').notNull(),
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true }),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    roleIdx: index('users_role_idx').on(t.role),
  }),
)

// contracts — top-level entity for multi-contract support.
export const contracts = pgTable('contracts', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  code: varchar('code', { length: 50 }).notNull().unique(),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: createdAtColumn(),
  updatedAt: updatedAtColumn(),
})

// bases — top-level military installations.
export const bases = pgTable(
  'bases',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    contractId: uuid('contract_id')
      .notNull()
      .references(() => contracts.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    code: varchar('code', { length: 50 }).notNull(),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueContractCode: uniqueIndex('bases_contract_id_code_unique').on(
      t.contractId,
      t.code,
    ),
    contractIdx: index('bases_contract_id_idx').on(t.contractId),
  }),
)

export const sites = pgTable(
  'sites',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    baseId: uuid('base_id')
      .notNull()
      .references(() => bases.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    code: varchar('code', { length: 50 }).notNull(),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueBaseCode: uniqueIndex('sites_base_id_code_unique').on(
      t.baseId,
      t.code,
    ),
    baseIdx: index('sites_base_id_idx').on(t.baseId),
  }),
)

// buildings — floc stays nullable so field-added rows can omit it.
// `building_type_id` is nullable: pre-existing/seed-imported rows have
// no template (opt-in FK). Admin creates set it via the Building Type
// select; field-added rows stay null until the admin classifies.
export const buildings = pgTable(
  'buildings',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    siteId: uuid('site_id')
      .notNull()
      .references(() => sites.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    floc: varchar('floc', { length: 100 }).unique(),
    floorAreaM2: numeric('floor_area_m2', { precision: 10, scale: 2 }),
    status: buildingStatusEnum('status').notNull().default('not_started'),
    buildingTypeId: uuid('building_type_id').references(
      () => buildingTypes.id,
      { onDelete: 'restrict' },
    ),
    isFieldAdded: boolean('is_field_added').notNull().default(false),
    requiresReview: boolean('requires_review').notNull().default(false),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    idSitePairUnique: uniqueIndex('buildings_id_site_id_unique').on(
      t.id,
      t.siteId,
    ),
    siteIdx: index('buildings_site_id_idx').on(t.siteId),
    statusIdx: index('buildings_status_idx').on(t.status),
    buildingTypeIdx: index('buildings_building_type_id_idx').on(
      t.buildingTypeId,
    ),
  }),
)

// floors — admin-managed per building.
export const floors = pgTable(
  'floors',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    buildingId: uuid('building_id')
      .notNull()
      .references(() => buildings.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 100 }).notNull(),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    idBuildingPairUnique: uniqueIndex('floors_id_building_id_unique').on(
      t.id,
      t.buildingId,
    ),
    uniqueBuildingName: uniqueIndex('floors_building_id_name_unique').on(
      t.buildingId,
      t.name,
    ),
    buildingIdx: index('floors_building_id_idx').on(t.buildingId),
  }),
)

// floor_options lives in ./schema-floor-options.ts (kept out of this
// file so the 500-line policy cap stays clear of the legacy tables).

// room_types — contract-scoped room categories and pseudo-space types.
export const roomTypes = pgTable(
  'room_types',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    contractId: uuid('contract_id')
      .notNull()
      .references(() => contracts.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    isSerialisable: boolean('is_serialisable').notNull().default(true),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueContractName: uniqueIndex('room_types_contract_id_name_unique').on(
      t.contractId,
      t.name,
    ),
    contractIdx: index('room_types_contract_id_idx').on(t.contractId),
  }),
)

// spaces — display_name is app-assigned and pseudo-spaces never carry a floor. `createdInSessionId` scopes cancel-session orphan cleanup (PR #548).
export const spaces = pgTable(
  'spaces',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    buildingId: uuid('building_id')
      .notNull()
      .references(() => buildings.id, { onDelete: 'restrict' }),
    floorId: uuid('floor_id'),
    roomTypeId: uuid('room_type_id')
      .notNull()
      .references(() => roomTypes.id, { onDelete: 'restrict' }),
    serialNumber: integer('serial_number').notNull(),
    displayName: varchar('display_name', { length: 255 }).notNull(),
    // Optional alias supplementing displayName; nullable, never replaces it. Set by admin OR field (FFR2); exported as space_alias, CSV col 7 (ADR-016).
    alias: varchar('alias', { length: 255 }),
    isPseudo: boolean('is_pseudo').notNull().default(false),
    isFieldAdded: boolean('is_field_added').notNull().default(false),
    requiresReview: boolean('requires_review').notNull().default(false),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
    createdInSessionId: uuid('created_in_session_id').references(() => captureSessions.id, { onDelete: 'set null' }),
    // Which building type seeded this room. NULL for field-created,
    // seed-imported, or pre-marker rooms — which are otherwise
    // indistinguishable from template-seeded rooms (same null session,
    // false flags, no items). Lets un-assigning / changing a building type
    // remove only the rooms IT added, never a real survey room. ON DELETE
    // SET NULL: deleting a building type just unmarks its rooms.
    seededFromBuildingTypeId: uuid('seeded_from_building_type_id').references(
      () => buildingTypes.id,
      { onDelete: 'set null' },
    ),
  },
  (t) => ({
    idBuildingPairUnique: uniqueIndex('spaces_id_building_id_unique').on(
      t.id,
      t.buildingId,
    ),
    seededFromIdx: index('spaces_seeded_from_building_type_id_idx')
      .on(t.seededFromBuildingTypeId)
      .where(sql`${t.seededFromBuildingTypeId} IS NOT NULL`),
    uniqueSerial: uniqueIndex('spaces_building_room_type_serial_unique').on(
      t.buildingId,
      t.roomTypeId,
      t.serialNumber,
    ),
    uniquePseudo: uniqueIndex('spaces_pseudo_per_building_unique')
      .on(t.buildingId, t.roomTypeId)
      .where(sql`${t.isPseudo} = true`),
    buildingIdx: index('spaces_building_id_idx').on(t.buildingId),
    floorIdx: index('spaces_floor_id_idx')
      .on(t.floorId)
      .where(sql`${t.floorId} IS NOT NULL`),
    serialNumberPositive: check(
      'spaces_serial_number_positive_check',
      sql`${t.serialNumber} > 0`,
    ),
    floorScopeCheck: check(
      'spaces_floor_scope_check',
      sql`(${t.isPseudo} = true AND ${t.floorId} IS NULL) OR (${t.isPseudo} = false AND ${t.floorId} IS NOT NULL)`,
    ),
    floorBelongsToBuilding: foreignKey({
      columns: [t.floorId, t.buildingId],
      foreignColumns: [floors.id, floors.buildingId],
      name: 'spaces_floor_building_consistency_fk',
    }).onDelete('restrict'),
  }),
)

// asset classification hierarchy: systems → sub-systems → types.
// All three levels carry rejected_at for soft-delete; cascade-filter in
// reads hides a rejected parent's descendants even if their own
// rejected_at is null.
export const assetSystems = pgTable(
  'asset_systems',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    contractId: uuid('contract_id')
      .notNull()
      .references(() => contracts.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    code: varchar('code', { length: 50 }),
    displayOrder: integer('display_order').notNull().default(0),
    rejectedAt: timestamp('rejected_at', { withTimezone: true }),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueContractName: uniqueIndex(
      'asset_systems_contract_id_name_unique',
    ).on(t.contractId, t.name),
    contractIdx: index('asset_systems_contract_id_idx').on(t.contractId),
  }),
)

export const assetSubSystems = pgTable(
  'asset_sub_systems',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    systemId: uuid('system_id')
      .notNull()
      .references(() => assetSystems.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    code: varchar('code', { length: 50 }),
    displayOrder: integer('display_order').notNull().default(0),
    rejectedAt: timestamp('rejected_at', { withTimezone: true }),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueSystemName: uniqueIndex(
      'asset_sub_systems_system_id_name_unique',
    ).on(t.systemId, t.name),
    systemIdx: index('asset_sub_systems_system_id_idx').on(t.systemId),
  }),
)

// asset_types — soft-delete via rejected_at preserves audit history.
export const assetTypes = pgTable(
  'asset_types',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    subSystemId: uuid('sub_system_id')
      .notNull()
      .references(() => assetSubSystems.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    code: varchar('code', { length: 50 }),
    unitOfMeasure: unitOfMeasureEnum('unit_of_measure').notNull(),
    groupingConvention: groupingConventionEnum('grouping_convention')
      .notNull()
      .default('1 asset / space'),
    isFieldAdded: boolean('is_field_added').notNull().default(false),
    requiresReview: boolean('requires_review').notNull().default(false),
    rejectedAt: timestamp('rejected_at', { withTimezone: true }),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueSubSystemName: uniqueIndex(
      'asset_types_sub_system_id_name_unique',
    ).on(t.subSystemId, t.name),
    subSystemIdx: index('asset_types_sub_system_id_idx').on(t.subSystemId),
    reviewQueueIdx: index('asset_types_review_queue_idx')
      .on(t.requiresReview)
      .where(
        sql`${t.requiresReview} = true AND ${t.rejectedAt} IS NULL`,
      ),
  }),
)

// room_type_assets — template rows pre-populated into capture lists.
export const roomTypeAssets = pgTable(
  'room_type_assets',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    roomTypeId: uuid('room_type_id')
      .notNull()
      .references(() => roomTypes.id, { onDelete: 'cascade' }),
    assetTypeId: uuid('asset_type_id')
      .notNull()
      .references(() => assetTypes.id, { onDelete: 'restrict' }),
    expectedQuantity: numeric('expected_quantity', { precision: 10, scale: 2 }),
    displayOrder: integer('display_order').notNull().default(0),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    uniqueRoomTypeAsset: uniqueIndex(
      'room_type_assets_room_type_asset_unique',
    ).on(t.roomTypeId, t.assetTypeId),
    roomTypeIdx: index('room_type_assets_room_type_id_idx').on(t.roomTypeId),
    assetTypeIdx: index('room_type_assets_asset_type_id_idx').on(t.assetTypeId),
  }),
)

// capture_sessions — building visits use site_id + building_id; site-only
// infrastructure capture uses site_id alone.
export const captureSessions = pgTable(
  'capture_sessions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    siteId: uuid('site_id')
      .notNull()
      .references(() => sites.id, { onDelete: 'restrict' }),
    buildingId: uuid('building_id'),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    userDisplayName: varchar('user_display_name', { length: 255 }).notNull(),
    startedAt: timestamp('started_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    sourceDeviceId: varchar('source_device_id', { length: 255 }),
    syncedAt: timestamp('synced_at', { withTimezone: true }),
    syncVersion: integer('sync_version').notNull().default(1),
    // Legacy PowerSync recency-window flag (migration 0020). RETAINED BUT
    // UNUSED since the per-user captures bucket (capture_items.user_id,
    // migration 0021): the sync rules no longer read it and nothing writes
    // it. Kept additive rather than dropped; remove in a later acknowledged
    // migration.
    inSyncWindow: boolean('in_sync_window').notNull().default(true),
    createdAt: createdAtColumn(),
    updatedAt: updatedAtColumn(),
  },
  (t) => ({
    idSitePairUnique: uniqueIndex('capture_sessions_id_site_id_unique').on(
      t.id,
      t.siteId,
    ),
    idBuildingPairUnique: uniqueIndex(
      'capture_sessions_id_building_id_unique',
    ).on(t.id, t.buildingId),
    siteIdx: index('capture_sessions_site_id_idx').on(t.siteId),
    buildingIdx: index('capture_sessions_building_id_idx')
      .on(t.buildingId)
      .where(sql`${t.buildingId} IS NOT NULL`),
    userIdx: index('capture_sessions_user_id_idx').on(t.userId),
    activeIdx: index('capture_sessions_active_idx')
      .on(t.buildingId)
      .where(
        sql`${t.completedAt} IS NULL AND ${t.buildingId} IS NOT NULL`,
      ),
    buildingBelongsToSite: foreignKey({
      columns: [t.buildingId, t.siteId],
      foreignColumns: [buildings.id, buildings.siteId],
      name: 'capture_sessions_building_site_consistency_fk',
    }).onDelete('restrict'),
  }),
)

// `buildingTypes` and `buildingTypeRoomTypes` live in
// ./schema-templates.ts; `roomTypeAssetExclusions` lives in
// ./schema-exclusions.ts. Both are re-exported from
// `@citycare/shared` so consumers see one import path.
